import { useEffect, useMemo, useRef, useState } from "react";
import { Aperture, ArrowUpRight, ShieldCheck, Telescope, Truck } from "lucide-react";
import { nextShowcaseIndex, shouldRotateShowcase, showcaseSlides, SHOWCASE_INTERVAL_MS } from "./data/showcase";

export default function HeroShowcase({ onExplore }: { onExplore: () => void }) {
  const root = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState<number[]>([]);
  const [manuallySelected, setManuallySelected] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(() => document.visibilityState === "visible");
  const [inView, setInView] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(() => matchMedia("(prefers-reduced-motion: reduce)").matches);
  const available = useMemo(() => [...loaded].sort((a, b) => a - b), [loaded]);
  const rotating = shouldRotateShowcase({ reducedMotion, manuallySelected, hovered, focused, visible, inView, availableCount: available.length });
  const slide = showcaseSlides[index];
  const FeatureIcon = index === 0 ? ShieldCheck : index === 1 ? Truck : Telescope;

  useEffect(() => {
    const preference = matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setReducedMotion(preference.matches);
    const updateVisibility = () => setVisible(document.visibilityState === "visible");
    preference.addEventListener("change", updatePreference);
    document.addEventListener("visibilitychange", updateVisibility);
    const observer = "IntersectionObserver" in window ? new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: .15 }) : null;
    if (root.current) observer?.observe(root.current);
    return () => {
      preference.removeEventListener("change", updatePreference);
      document.removeEventListener("visibilitychange", updateVisibility);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!rotating) return;
    const timer = window.setTimeout(() => setIndex((current) => nextShowcaseIndex(current, available)), SHOWCASE_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [index, available, rotating]);

  function imageReady(imageIndex: number) {
    setLoaded((current) => current.includes(imageIndex) ? current : [...current, imageIndex]);
  }
  function imageFailed(imageIndex: number) {
    setLoaded((current) => current.filter((item) => item !== imageIndex));
    if (imageIndex === index) setIndex((current) => nextShowcaseIndex(current, available.filter((item) => item !== imageIndex)));
  }

  return <div ref={root} className={`hero-visual hero-showcase${rotating ? " is-rotating" : ""}`}
    role="region" aria-label="Vetrina fotografia e astronomia" aria-roledescription="carosello" aria-describedby="showcase-instructions"
    onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
    onFocusCapture={() => setFocused(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false); }}>
    {showcaseSlides.map((item, itemIndex) => <img key={item.id} className={`showcase-photo${itemIndex === index ? " is-current" : ""}`}
      src={item.image} alt={itemIndex === index ? item.alt : ""} aria-hidden={itemIndex !== index}
      style={{ objectPosition: item.position }} fetchPriority={itemIndex === 0 ? "high" : "low"}
      onLoad={() => imageReady(itemIndex)} onError={() => imageFailed(itemIndex)} />)}
    <div className="hero-visual-tint" />
    <div key={slide.id} className="showcase-copy" aria-live="off">
      <span className="hero-photo-label"><Aperture size={14} aria-hidden="true" />{slide.eyebrow}</span>
      <div className="hero-image-caption"><span>{slide.intro}</span><strong>{slide.headline}</strong></div>
      <button className="showcase-feature" onClick={onExplore}><FeatureIcon size={22} aria-hidden="true" /><span><strong>{slide.feature}</strong><small>{slide.detail}</small></span><ArrowUpRight size={18} aria-hidden="true" /></button>
    </div>
    <p id="showcase-instructions" className="showcase-instructions">Le immagini cambiano automaticamente. Scegli una categoria per mantenerla sullo schermo. La rotazione si ferma anche durante il focus e al passaggio del puntatore.</p>
    <div className="showcase-navigation" role="group" aria-label="Scegli una categoria e mantieni l’immagine">
      {showcaseSlides.map((item, itemIndex) => <button key={item.id} className={itemIndex === index ? "is-current" : ""} aria-pressed={itemIndex === index}
        aria-label={`Mostra ${item.label} e mantieni l’immagine`} disabled={!loaded.includes(itemIndex)} onClick={() => { setManuallySelected(true); setIndex(itemIndex); }}>
        <span className="showcase-track"><span key={`${item.id}-${index}-${rotating}`} /></span><span>{item.label}</span>
      </button>)}
    </div>
  </div>;
}
